// Port of SuiteModel/ProseMarkdown+CommonMark.swift: ordinary Markdown, as agents and other apps write it, in the prose
// dialect. Headings become bold paragraphs; lists the dialect's lists (bulleted, or numbered from their first number);
// links to web or mail addresses stay links (others their words); images and HTML comments nothing; code its text; a
// thematic break (`---`) a scene break. Emphasis (`*`, `_`), block quotes, footnotes and hard breaks (two trailing
// spaces or `\`) are kept. Swift's Regex `\s`, `\d` and `\w` are Unicode-aware, so they're written here as Unicode
// properties.
import { escape, isLinkAddress, linkAddress, parse, parseListMarker, serialize } from './proseMarkdown.ts';
import { trimCFWhitespace } from './unicode.ts';

const S = String.raw`[\p{White_Space}]`;
const W = String.raw`[\p{L}\p{M}\p{Nd}\p{Pc}]`;
const HEADING = new RegExp(String.raw`^#{1,6}${S}+(.*?)${S}*#*$`, 'u');
const RULE = new RegExp(String.raw`^([-*_])(${S}*\1){2,}$`, 'u');
const BULLET = new RegExp(String.raw`^[-*+]${S}+(.*)$`, 'u');
const NUMBERED = new RegExp(String.raw`^(\p{Nd}{1,9})([.)])${S}+(.*)$`, 'u');
const QUOTE = new RegExp(String.raw`^>${S}?(.*)$`, 'u');

/** `ProseMarkdown.fromCommonMark`. */
export function fromCommonMark(markdown: string): string {
  const blocks: string[] = [];
  let lines: string[] = []; // the paragraph (or list item, or quote) being gathered
  let inFence = false;
  let breakAfterLast = false;
  const flush = () => {
    if (lines.length) blocks.push(lines.join('\n'));
    lines = [];
    breakAfterLast = false;
  };
  // A list's items stay together, a line each.
  const startItem = (item: string) => {
    if (lines.length && parseListMarker(lines[0]) !== null) breakAfterLast = false;
    else flush();
    lines.push(item);
  };
  const source = markdown.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
  for (const raw of source.split('\n')) {
    const line = trimCFWhitespace(raw);
    if (line.startsWith('```') || line.startsWith('~~~')) {
      flush();
      inFence = !inFence;
      continue;
    }
    if (inFence) {
      // Code keeps its lines, as text.
      if (lines.length) lines[lines.length - 1] += '\\';
      lines.push(escape(trimCFWhitespace(raw)));
      continue;
    }
    if (line === '') { flush(); continue; }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      const text = inline(heading[1]);
      if (text !== '') blocks.push(`**${text}**`);
      continue;
    }
    if (RULE.test(line)) {
      flush();
      blocks.push('***');
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
      startItem('•\t' + inline(bullet[1]));
      continue;
    }
    const numbered = NUMBERED.exec(line);
    if (numbered) {
      startItem(`${numbered[1]}${numbered[2]}\t` + inline(numbered[3]));
      continue;
    }
    const quote = QUOTE.exec(line);
    if (quote) {
      if (lines.length && !lines[0].startsWith('> ')) flush();
      lines.push('> ' + inline(quote[1]));
      continue;
    }
    if (line.startsWith('[^') && line.includes(']:')) {
      flush();
      blocks.push(line);
      continue;
    }
    // A quote ends at a line that isn't one; a hard break ends the line before the next.
    if (lines.length && lines[0].startsWith('> ')) flush();
    if (breakAfterLast && lines.length) lines[lines.length - 1] += '\\';
    lines.push(inline(line.endsWith('\\') ? line.slice(0, -1) : line));
    breakAfterLast = raw.endsWith('  ') || line.endsWith('\\');
  }
  flush();
  return serialize(parse(blocks.join('\n\n')));
}

const COMMENT = /<!--.*?-->/gu;
const IMAGE = /!\[[^\]]*\]\([^)]*\)/gu;
const LINK = new RegExp(String.raw`\[([^\]^][^\]]*)\]\(${S}*<?([^)\p{White_Space}>]*)>?(?:${S}+"[^"]*")?${S}*\)`, 'gu');
const AUTOLINK = /<((?:https?:\/\/|mailto:)[^>\p{White_Space}]+)>/gu;
const CODE = /`([^`]+)`/gu;
const STRUCK = /~~(.+?)~~/gu;
const STRONG = new RegExp(String.raw`(^|[^\p{L}\p{M}\p{Nd}\p{Pc}\\])__(\S(?:.*?\S)?)__(?!${W})`, 'gu');
const EMPHASIS = new RegExp(String.raw`(^|[^\p{L}\p{M}\p{Nd}\p{Pc}\\])_(\S(?:.*?\S)?)_(?!${W})`, 'gu');
// Links finished early are set aside behind these (U+E002 n U+E003) until the end.
const KEEP_OPEN = String.fromCodePoint(0xe002), KEEP_CLOSE = String.fromCodePoint(0xe003);
const KEPT = new RegExp(KEEP_OPEN + String.raw`(\p{Nd}+)` + KEEP_CLOSE, 'gu');

/** `inline`: links stay links when they go to a web or mail address (their words alone otherwise, and a title is
 *  dropped), autolinks become links showing their address, images and HTML comments go, code keeps its text,
 *  `__bold__`/`_italic_` become asterisks, `~~struck~~` its words. */
export function inline(text: string): string {
  let s = text;
  // Links finished here are set aside until the end, so the rules after them never touch an address.
  const kept: string[] = [];
  const keep = (link: string) => {
    kept.push(link);
    return `${KEEP_OPEN}${kept.length - 1}${KEEP_CLOSE}`;
  };
  s = s.replace(COMMENT, '');
  s = s.replace(IMAGE, '');
  s = s.replace(LINK, (_m, words: string, target: string) => {
    const address = linkAddress(target);
    return isLinkAddress(address) ? `[${words}` + keep(`](${address})`) : words;
  });
  s = s.replace(AUTOLINK, (_m, url: string) => {
    const address = linkAddress(url);
    const shown = url.startsWith('mailto:') ? url.slice(7) : url;
    return isLinkAddress(address) ? keep(`[${escape(shown)}](${address})`) : url;
  });
  s = s.replace(CODE, (_m, code: string) => escape(code));
  s = s.replace(STRUCK, (_m, words: string) => words);
  // Underscores only as emphasis, never inside a word (snake_case).
  s = s.replace(STRONG, (_m, before: string, inner: string) => `${before}**${inner}**`);
  s = s.replace(EMPHASIS, (_m, before: string, inner: string) => `${before}*${inner}*`);
  s = s.replace(KEPT, (_m, n: string) => (/^[0-9]+$/.test(n) ? kept[Number(n)] ?? '' : ''));
  return trimCFWhitespace(s);
}
